import { useState } from 'react';
import { useMatch } from 'react-router-dom';
import { useAuth } from '@screentone/addon-auth-wrapper';
import { Dropdown, Group, IconEye, Loader, Typography, Wrapper } from '@screentone/core';

import type { HistoryContent } from 'components/publish-history/types';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { SectionContainer } from 'data/generated/graphql';
import { OKTA_ENV } from 'features/auth/components/OktaRouter';
import { usePagePreview } from 'features/section-page-edit/hooks/usePagePreview';
import useIsNewSectionPage from 'hooks/useIsNewSectionPage';
import env from 'utils/env';

interface PreviewProps {
  selectedVersion?: HistoryContent;
}

const getPreviewUrl = (previewLink: string) => {
  if (previewLink.includes('/arts-culture')) {
    return previewLink.replace('/opinion', '');
  }

  return previewLink;
};

const WSJ_HOME_MOBILE_WEB_PROD_PREVIEW_URL =
  'https://article-preview.wsj.com/preview?previewCollectionId=Mobile_Section_wsj_us_WEB_NOW_TOP_NEWS_PROD&next_redirect=9911';

const WSJ_HOME_MOBILE_WEB_NON_PROD_PREVIEW_URL =
  'https://pr-2910.www.dev.wsj.com/preview?previewCollectionId=Mobile_Section_wsj_us_WEB_NOW_TOP_NEWS_BETA&next_redirect=9911';


export const Preview = ({ selectedVersion }: PreviewProps) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const { root } = useDataModelContext<SectionContainer>();
  const { handlePreviewPage, isLoading: isPreviewPageLoading, previewLink } = usePagePreview();
  const { currentProperty } = useAuth();
  const isCreatingPage = useIsNewSectionPage();
  const isWsjOpinion = currentProperty === 'wsjopinion';

  const match = useMatch('/wsj/pages/:pageId');
  const isWsjDesktop = match?.params.pageId?.toLowerCase().includes('desktop') ?? false;

  const handlePreview = async () => {
    setPreviewOpen(!previewOpen);
    await handlePreviewPage(selectedVersion);
  };

  const getHostname = () => {
    const host = isWsjOpinion ? 'wsj' : currentProperty;
    const path = isWsjOpinion && root.attributes.pageType?.toLowerCase() === 'home' ? '/opinion' : '';

    return `${host?.toUpperCase()}.com${path}`;
  };

  const defaultPreviewUrl = getPreviewUrl(previewLink ?? '');

  const isWsjHomePage = root.attributes.pageType === 'HOME' && currentProperty === 'wsj' && !isWsjDesktop;

  const mobileWebPreviewUrl =
    env.OKTA_ENV === OKTA_ENV.PRD ? WSJ_HOME_MOBILE_WEB_PROD_PREVIEW_URL : WSJ_HOME_MOBILE_WEB_NON_PROD_PREVIEW_URL;

  return (
    <Dropdown
      trigger={
        <Group gap="sm">
          <IconEye />
          Preview
        </Group>
      }
      position="right"
      onClick={handlePreview}
      disabled={isCreatingPage}
    >
      {isPreviewPageLoading ? (
        <Loader size="md" />
      ) : (
        <Wrapper padding={{ all: 'sm' }}>
          <Typography style={{ width: '350px' }}>
            {' '}
            To preview {(root.attributes.pageType ?? root.type).toLowerCase()} page on{' '}
            <Typography inline weight="med">
              {getHostname()}
            </Typography>
            , please click{' '}
            <Typography variant="link" componentEl="a" href={defaultPreviewUrl} target="_blank" inline>
              here.
            </Typography>
          </Typography>

          {isWsjHomePage && (
            <Typography>
              To preview the page on the{' '}
              <Typography inline weight="med">
                mobile WEB
              </Typography>
              , please click{' '}
              <Typography componentEl="a" variant="link" href={mobileWebPreviewUrl} target="_blank" inline>
                here.
              </Typography>
            </Typography>
            )}

            {!isWsjDesktop && (
              <Typography>
                To preview the page in the{' '}
                <Typography inline weight="med">
                  mobile app
                </Typography>
                , please check out the proofing build.
              </Typography>
          )}
        </Wrapper>
      )}
    </Dropdown>
  );
};
